/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiAddLine, RiArrowRightLine } from "react-icons/ri";
import { FormattedMessage, IntlFormatters, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useHasMoved } from "../buffered-data-hooks";
import { actionNameInputId } from "../components/ActionNameCard";
import DataSamplesTable from "../components/DataSamplesTable";
import {
  AddActionHint,
  MoveMicrobitHint,
  moveMicrobitHintTimeoutInSec,
  TrainHint,
} from "../components/DataSamplesTableHints";
import DefaultPageLayout, {
  ProjectToolbarItems,
} from "../components/DefaultPageLayout";
import LiveGraphPanel from "../components/LiveGraphPanel";
import TrainModelDialogs from "../components/TrainModelFlowDialogs";
import WelcomeDialog from "../components/WelcomeDialog";
import {
  isDataConnectionDialogOpen,
  useDataConnected,
} from "../data-connection-flow";
import { keyboardShortcuts, useShortcut } from "../keyboard-shortcut-hooks";
import { useLiveRegion } from "../live-region-hook";
import {
  ActionData,
  DataSamplesPageHint,
  PostImportDialogState,
} from "../model";
import { projectSessionStorage } from "../session-storage";
import { Button, css, cx, Flex, HStack, Icon } from "@microbit/ui";
import {
  useHasSufficientDataForTraining,
  useSettings,
  useStore,
} from "../store";
import PageActionBar from "../components/PageActionBar";
import { tourElClassname } from "../tours";
import { createHomePageUrl, createTestingModelPageUrl } from "../urls";

const DataSamplesPage = () => {
  const actions = useStore((s) => s.actions);
  const addNewAction = useStore((s) => s.addNewAction);
  const model = useStore((s) => s.model);
  const projectId = useStore((s) => s.id);
  const [selectedActionIdx, setSelectedActionIdx] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectSessionStorage.getProjectId() || !projectId) {
      void navigate(createHomePageUrl());
    }
  }, [navigate, projectId]);

  const trainModelFlowStart = useStore((s) => s.trainModelFlowStart);

  const tourStart = useStore((s) => s.tourStart);
  const isConnected = useDataConnected();
  const isConnectionDialogOpen = useStore((s) =>
    isDataConnectionDialogOpen(s.dataConnection.step)
  );
  useEffect(() => {
    // If a user first connects on "Testing model" this can result in the tour when they return to the "Data samples" page.
    if (isConnected) {
      void tourStart({ name: "Connect" }, false);
    }
  }, [isConnected, tourStart]);

  const hasSufficientData = useHasSufficientDataForTraining();
  const isAddNewActionDisabled = actions.some((a) => a.name.length === 0);

  const handleNavigateToModel = useCallback(() => {
    void navigate(createTestingModelPageUrl());
  }, [navigate]);

  const trainButtonRef = useRef(null);
  const handleAddNewAction = useCallback(async () => {
    setSelectedActionIdx(actions.length);
    await addNewAction();
    requestAnimationFrame(() => {
      const updatedActions = useStore.getState().actions;
      const newAction = updatedActions[updatedActions.length - 1];
      if (newAction) {
        document
          .getElementById(actionNameInputId(newAction))
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  }, [addNewAction, actions]);
  useShortcut(keyboardShortcuts.addAction, handleAddNewAction, {
    enabled: !isAddNewActionDisabled,
  });
  const intl = useIntl();
  const welcomeDialogDismissedForProject = useStore(
    (s) => s.welcomeDialogDismissedForProject
  );
  const dismissWelcomeDialog = useStore((s) => s.dismissWelcomeDialog);
  const isWelcomeDialogOpen =
    !isConnected && welcomeDialogDismissedForProject !== projectId;
  // Lock in the decision to skip the welcome dialog when arriving already
  // connected so that a later disconnection doesn't resurface it.
  useEffect(() => {
    if (isConnected && welcomeDialogDismissedForProject !== projectId) {
      dismissWelcomeDialog();
    }
  }, [
    isConnected,
    projectId,
    welcomeDialogDismissedForProject,
    dismissWelcomeDialog,
  ]);
  const hasMoved = useHasMoved();
  const tourState = useStore((s) => s.tourState);
  const isRecordingDialogOpen = useStore((s) => !!s.isRecordingDialogOpen);
  const isPostImportDialogOpen = useStore(
    (s) => s.postImportDialogState !== PostImportDialogState.None
  );
  const tourInProgress = !!tourState;
  const isDialogOpen =
    isWelcomeDialogOpen ||
    isConnectionDialogOpen ||
    tourInProgress ||
    isRecordingDialogOpen ||
    isPostImportDialogOpen;

  const hint = useStore((s) => s.hint);
  const setHint = useStore((s) => s.setHint);
  useEffect(() => {
    // Initialise hint on first load.
    setHint(true);
  }, [setHint]);
  const dataSamplesHint: DataSamplesPageHint = useMemo(
    () =>
      isDialogOpen
        ? null
        : isConnected && !hasMoved
        ? { type: "move-microbit" }
        : hint,
    [hasMoved, hint, isConnected, isDialogOpen]
  );

  const pageRef = useRef(null);
  const region = useLiveRegion(pageRef.current);

  // To avoid aria-live interruptions, particularly when inputting action name.
  const debouncedSpeakHint = useMemo(
    () =>
      debounce(
        (hintText: string) => {
          region.speak(hintText);
        },
        1000,
        { leading: false, trailing: true }
      ),
    [region]
  );

  const setHasMoved = useStore((s) => s.setHasMoved);
  const [settings] = useSettings();
  useEffect(() => {
    if (
      dataSamplesHint?.type === "move-microbit" &&
      settings.toursCompleted.includes("Connect")
    ) {
      const id = setTimeout(
        () => setHasMoved(true),
        moveMicrobitHintTimeoutInSec * 1000
      );
      return () => clearTimeout(id);
    }
  }, [dataSamplesHint?.type, setHasMoved, settings.toursCompleted]);

  useEffect(() => {
    if (!dataSamplesHint) {
      return;
    }
    const actionWithHint = actions[actions.length - 1];
    const hintText = getHintText(
      intl,
      dataSamplesHint,
      isConnected,
      actionWithHint
    );
    debouncedSpeakHint(hintText);
  }, [actions, dataSamplesHint, debouncedSpeakHint, intl, isConnected]);

  return (
    <>
      {isWelcomeDialogOpen && !isPostImportDialogOpen && (
        <WelcomeDialog
          onClose={dismissWelcomeDialog}
          isOpen={isWelcomeDialogOpen}
        />
      )}
      <TrainModelDialogs finalFocusRef={trainButtonRef} />
      <DefaultPageLayout
        titleId="data-samples-title"
        showProjectName
        showPageTitle
        toolbarItemsRight={<ProjectToolbarItems />}
        backUrl={createHomePageUrl()}
        bottomContent={
          <>
            <PageActionBar
              aria-label={intl.formatMessage({
                id: "data-samples-actions-region",
              })}
              css={{ position: "relative" }}
            >
              <HStack gap={2} alignItems="center">
                <Button
                  className={tourElClassname.addActionButton}
                  variant={hasSufficientData ? "secondary" : "primary"}
                  startIcon={<Icon as={RiAddLine} />}
                  onPress={handleAddNewAction}
                  isDisabled={isAddNewActionDisabled}
                >
                  <FormattedMessage id="add-action-action" />
                </Button>
              </HStack>
              {dataSamplesHint?.type === "add-action" && (
                <AddActionHint action={actions[0]} />
              )}
              <HStack position="relative">
                {dataSamplesHint?.type === "train" && <TrainHint />}
                {model ? (
                  <Button
                    onPress={handleNavigateToModel}
                    className={tourElClassname.trainModelButton}
                    variant="primary"
                    endIcon={<Icon as={RiArrowRightLine} />}
                  >
                    <FormattedMessage id="testing-model-title" />
                  </Button>
                ) : (
                  <Button
                    ref={trainButtonRef}
                    className={cx(
                      tourElClassname.trainModelButton,
                      hasSufficientData && !isRecordingDialogOpen
                        ? css({
                            animation: "tada 1s ease-in-out",
                            _motionReduce: {
                              animation: "none",
                            },
                          })
                        : undefined
                    )}
                    onPress={() => trainModelFlowStart(handleNavigateToModel)}
                    variant={
                      hasSufficientData ? "primary" : "secondary-disabled"
                    }
                  >
                    <FormattedMessage id="train-model" />
                  </Button>
                )}
              </HStack>
              {dataSamplesHint?.type === "move-microbit" && (
                <MoveMicrobitHint />
              )}
            </PageActionBar>
            <LiveGraphPanel
              disconnectedTextId="connect-to-record"
              showDisconnectedOverlay={!isDialogOpen}
            />
          </>
        }
        backLabelId="home-action"
      >
        <Flex
          as="main"
          flexGrow={1}
          flexDir="column"
          ref={pageRef}
          w="max-content"
          minWidth="100%"
        >
          <DataSamplesTable
            selectedActionIdx={selectedActionIdx}
            setSelectedActionIdx={setSelectedActionIdx}
            hint={dataSamplesHint}
          />
        </Flex>
      </DefaultPageLayout>
    </>
  );
};

const getHintText = (
  intl: IntlFormatters,
  hint: DataSamplesPageHint,
  isConnected: boolean,
  action: ActionData
): string => {
  if (!hint) {
    return "";
  }
  switch (hint.type) {
    case "add-action": {
      return intl.formatMessage(
        { id: "add-action-hint-label" },
        { actionName: action.name }
      );
    }
    case "move-microbit": {
      return intl.formatMessage({ id: "move-hint" });
    }
    case "record-action": {
      return isConnected
        ? intl.formatMessage(
            { id: "record-hint-button-b" },
            { mark: (chunks: string[]) => chunks.join("") }
          )
        : intl.formatMessage({ id: "record-hint" });
    }
    case "name-action-short":
    case "name-action-with-samples":
    case "name-action": {
      return intl.formatMessage({ id: "name-action-hint" });
    }
    case "record-more-action": {
      return intl.formatMessage(
        { id: "record-more-hint-label" },
        {
          numSamples: action.recordings.length === 1 ? 2 : 1,
          actionName: action.name,
        }
      );
    }
    case "train": {
      return intl.formatMessage({ id: "train-hint-label" });
    }
  }
};

export default DataSamplesPage;
